import { useMemo } from "react"
import type { Query as TCubeQuery } from "@cubejs-client/core"

import {
  resolveWeekStartDay,
  weekGranularityName,
  type WeekStartDay,
} from "~/shared/kpi"
import { useCompanyControllerGetFinancialsQuery } from "~/ui-rtk/api/companyApi"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import { resolveTimeDimensionKey } from "~/ui-rtk/utils/cube"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"

import { COHORT_CUBES, type CohortFamily } from "./constants"
import {
  buildCohortRows,
  maxMatureOffset,
  toMatrixRow,
  type CohortGranularity,
  type CohortRow,
} from "./utils/cohort-matrix"
import { currentWeekStart } from "./utils/period-presets"

const dayjs = useDate()

export type LtvCohortFilters = {
  channels: string[]
  valueBands: string[]
  family: CohortFamily
  productIds: string[]
  collectionIds: string[]
  skuPrefixLength: number
  skuPrefixes: string[]
}

const MEASURES = [
  "net_revenue",
  "first_order_revenue",
  "new_customers",
  "active_customers",
  "orders",
] as const

// Well above the theoretical (cohort bucket x activity bucket) pair count
// for any supported window (a 52-week window is ~53 * ~60 pairs).
const MATRIX_ROW_LIMIT = 50_000
const OPTIONS_LIMIT = 1_000

/**
 * The FAMILY cube is only queried when the user actually picked values —
 * an empty product selection falls back to the base cube, because the family
 * tables cover a narrower population (e.g. products in no collection are
 * absent from the collection table) and would silently shift the totals.
 */
export function effectiveFamily(filters: LtvCohortFilters): CohortFamily {
  if (filters.family === "product" && filters.productIds.length > 0) {
    return "product"
  }
  if (filters.family === "collection" && filters.collectionIds.length > 0) {
    return "collection"
  }
  if (filters.family === "sku" && filters.skuPrefixes.length > 0) {
    return "sku"
  }
  return "none"
}

function useOptionsQuery(
  cube: string,
  companyId: string | undefined,
  range: { start: string; end: string },
  valueDimension: string,
  labelDimension: string | null,
  extraFilters: Array<{ member: string; operator: "equals"; values: string[] }>,
  skip: boolean,
) {
  const query = useMemo<TCubeQuery>(
    () => ({
      dimensions: [
        `${cube}.${valueDimension}`,
        ...(labelDimension ? [`${cube}.${labelDimension}`] : []),
      ],
      measures: [`${cube}.net_revenue`],
      timeDimensions: [
        {
          dimension: `${cube}.first_order_date`,
          dateRange: [range.start, range.end],
        },
      ],
      filters: [
        {
          member: `${cube}.companyid`,
          operator: "equals",
          values: [companyId ?? ""],
        },
        ...extraFilters,
      ],
      order: { [`${cube}.net_revenue`]: "desc" },
      limit: OPTIONS_LIMIT,
    }),
    [
      cube,
      companyId,
      range.start,
      range.end,
      valueDimension,
      labelDimension,
      extraFilters,
    ],
  )

  const { resultSet, isLoading } = useCubeQuery(query, {
    skip: skip || !companyId,
  })

  const options = useMemo<TMultiSelectOption[]>(() => {
    const rows = (resultSet?.rawData() ?? []) as Array<Record<string, unknown>>
    const seen = new Map<string, TMultiSelectOption>()
    for (const row of rows) {
      const value = String(row[`${cube}.${valueDimension}`] ?? "")
      if (!value || seen.has(value)) continue
      const label = labelDimension
        ? String(row[`${cube}.${labelDimension}`] || value)
        : value
      seen.set(value, { value, label })
    }
    return Array.from(seen.values())
  }, [resultSet, cube, valueDimension, labelDimension])

  return { options, isLoading: skip ? false : isLoading }
}

export default function useConnect({
  granularity,
  range,
  filters,
}: {
  granularity: CohortGranularity
  range: { start: string; end: string }
  filters: LtvCohortFilters
}) {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id

  const { data: financials } = useCompanyControllerGetFinancialsQuery(
    { id: companyId ?? "" },
    { skip: !companyId },
  )
  const weekStartDay: WeekStartDay = resolveWeekStartDay(financials)

  const granularityName =
    granularity === "week" ? weekGranularityName(weekStartDay) : "month"

  const family = effectiveFamily(filters)
  const cube = COHORT_CUBES[family]

  const cubeFilters = useMemo(() => {
    const result: Array<{
      member: string
      operator: "equals"
      values: string[]
    }> = [
      {
        member: `${cube}.companyid`,
        operator: "equals",
        values: [companyId ?? ""],
      },
    ]
    if (filters.channels.length > 0) {
      result.push({
        member: `${cube}.first_order_channel`,
        operator: "equals",
        values: filters.channels,
      })
    }
    if (filters.valueBands.length > 0) {
      result.push({
        member: `${cube}.first_order_value_band`,
        operator: "equals",
        values: filters.valueBands,
      })
    }
    if (family === "product") {
      result.push({
        member: `${cube}.product_id`,
        operator: "equals",
        values: filters.productIds,
      })
    } else if (family === "collection") {
      result.push({
        member: `${cube}.collection_id`,
        operator: "equals",
        values: filters.collectionIds,
      })
    } else if (family === "sku") {
      result.push(
        {
          member: `${cube}.prefix_length`,
          operator: "equals",
          values: [String(filters.skuPrefixLength)],
        },
        {
          member: `${cube}.sku_prefix`,
          operator: "equals",
          values: filters.skuPrefixes,
        },
      )
    }
    return result
  }, [
    cube,
    companyId,
    family,
    filters.channels,
    filters.valueBands,
    filters.productIds,
    filters.collectionIds,
    filters.skuPrefixLength,
    filters.skuPrefixes,
  ])

  const matrixQuery = useMemo<TCubeQuery>(
    () => ({
      measures: MEASURES.map(measure => `${cube}.${measure}`),
      // The cast is load-bearing: custom reporting-week granularity names
      // (e.g. 'week_thursday') are valid server-side but absent from
      // @cubejs-client's TimeDimensionGranularity union.
      timeDimensions: [
        {
          dimension: `${cube}.first_order_date`,
          granularity: granularityName as "month",
          dateRange: [range.start, range.end],
        },
        {
          // No dateRange on purpose: the cohort's forward activity extends
          // past the cohort window all the way to today.
          dimension: `${cube}.activity_date`,
          granularity: granularityName as "month",
        },
      ],
      filters: cubeFilters,
      limit: MATRIX_ROW_LIMIT,
    }),
    [cube, granularityName, range.start, range.end, cubeFilters],
  )

  const { resultSet, isLoading, error } = useCubeQuery(matrixQuery, {
    skip: !companyId,
  })

  // Local calendar date (not UTC) — must agree with the page's maturity
  // masking so a bucket never unmasks early in the viewer's evening.
  const todayIso = dayjs().format("YYYY-MM-DD")

  // Offsets are derived from the WINDOW (not the data) so the column set is
  // stable while loading and across filter changes: the oldest possible
  // cohort bucket determines how many offsets can be mature at all.
  const oldestCohortBucket =
    granularity === "month"
      ? `${range.start.slice(0, 7)}-01`
      : currentWeekStart(range.start, weekStartDay)
  const highestOffset = maxMatureOffset(
    oldestCohortBucket,
    granularity,
    todayIso,
  )
  const offsets = useMemo(
    () => Array.from({ length: Math.max(highestOffset + 1, 0) }, (_, i) => i),
    [highestOffset],
  )

  const cohortRows = useMemo<CohortRow[]>(() => {
    const rows = (resultSet?.rawData() ?? []) as Array<Record<string, unknown>>
    if (rows.length === 0) return []
    const cohortKey = resolveTimeDimensionKey(
      rows,
      `${cube}.first_order_date`,
      `${cube}.first_order_date.${granularityName}`,
    )
    const activityKey = resolveTimeDimensionKey(
      rows,
      `${cube}.activity_date`,
      `${cube}.activity_date.${granularityName}`,
    )
    const matrixRows = rows.map(row =>
      toMatrixRow(row, {
        cohortKey,
        activityKey,
        netRevenueKey: `${cube}.net_revenue`,
        firstOrderRevenueKey: `${cube}.first_order_revenue`,
        newCustomersKey: `${cube}.new_customers`,
        activeCustomersKey: `${cube}.active_customers`,
        ordersKey: `${cube}.orders`,
      }),
    )
    return buildCohortRows(matrixRows, granularity, Math.max(highestOffset, 0))
  }, [resultSet, cube, granularityName, granularity, highestOffset])

  // Picker option sources. Options are intentionally NOT narrowed by the
  // channel/band filters so the lists stay stable while filtering.
  const skuLevelFilter = useMemo(
    () => [
      {
        member: `${COHORT_CUBES.sku}.prefix_length`,
        operator: "equals" as const,
        values: [String(filters.skuPrefixLength)],
      },
    ],
    [filters.skuPrefixLength],
  )
  const emptyExtraFilters = useMemo(() => [], [])

  const productOptions = useOptionsQuery(
    COHORT_CUBES.product,
    companyId,
    range,
    "product_id",
    "product_title",
    emptyExtraFilters,
    filters.family !== "product",
  )
  const collectionOptions = useOptionsQuery(
    COHORT_CUBES.collection,
    companyId,
    range,
    "collection_id",
    "collection_title",
    emptyExtraFilters,
    filters.family !== "collection",
  )
  const skuPrefixOptions = useOptionsQuery(
    COHORT_CUBES.sku,
    companyId,
    range,
    "sku_prefix",
    null,
    skuLevelFilter,
    filters.family !== "sku",
  )

  return {
    cohortRows,
    offsets,
    isLoading,
    error,
    todayIso,
    weekStartDay,
    productOptions,
    collectionOptions,
    skuPrefixOptions,
  }
}
